import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDroppable } from "@dnd-kit/core";
import { FolderPlus, History, Inbox, Layers, Search } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { api } from "@/lib/api";
import { FolderIcon, folderColorClasses } from "@/components/folderTheme";
import CreateFolderModal from "@/components/CreateFolderModal";

const SPECIAL_ALL = "__all__";
const SPECIAL_UNFOLDERED = "__unfoldered__";

/**
 * Phase FOLDER-UX (FU.2) — left rail listing every folder the current
 * user can see, plus the two synthetic rows "All workflows" and
 * "Unfoldered".  Doubles as the drop-target column for DnD in FU.3.
 *
 * Width by viewport:
 *   ≥ lg (1024px): 240px expanded with text + counts
 *   md (768-1023):  56px icon-rail (full text on hover via Tooltip)
 *   < md:           hidden entirely (kebab fallback remains)
 *
 * Selection model: `selectedFolderId` is either:
 *   null          → "All workflows"
 *   "__unfoldered__" → orphan workflows
 *   <folder_id>   → that specific folder
 */
export default function FoldersSidebar({
  selectedFolderId,
  onSelect,
  totals,
  testid = "folders-sidebar",
}) {
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  // Pull every folder the user can see (mine + org).  page_size=200 is
  // safe for the demo + a sensible upper bound for tenant clutter.
  const foldersQuery = useQuery({
    queryKey: ["folders-sidebar", "all"],
    queryFn: async () =>
      (await api.get("/folders?visibility=all&page_size=200")).data,
    staleTime: 60_000,
  });

  // R3 Phase 6A.7.3 — Recently Used pin.  Audit-driven aggregation
  // returns the top 3 folders this user dropped workflows into during
  // the last 7 days.  Empty list → section is hidden entirely.
  const recentlyUsedQuery = useQuery({
    queryKey: ["folders-recently-used"],
    queryFn: async () =>
      (await api.get("/folders/recently-used?window_days=7&limit=3")).data,
    staleTime: 60_000,
  });

  const folders = useMemo(
    () => foldersQuery.data?.items || [],
    [foldersQuery.data],
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return folders;
    return folders.filter((f) => f.name.toLowerCase().includes(q));
  }, [folders, search]);

  // Set of folder_ids surfaced in the Recently Used pin — used to mark
  // those same rows in the main list with a subtle "↗" hint.
  const recentlyUsed = useMemo(
    () => recentlyUsedQuery.data?.items || [],
    [recentlyUsedQuery.data],
  );
  const recentlyUsedIds = useMemo(
    () => new Set(recentlyUsed.map((r) => r.folder_id)),
    [recentlyUsed],
  );

  const allCount = totals?.all ?? null;
  const unfolderedCount = totals?.unfoldered ?? null;

  return (
    <TooltipProvider delayDuration={200}>
      <aside
        className="hidden h-fit shrink-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-slate-50 md:flex md:w-14 md:items-center md:gap-1 md:p-1.5 lg:w-60 lg:items-stretch lg:gap-2 lg:p-3"
        data-testid={testid}
      >
        <div className="hidden items-center justify-between lg:flex">
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Folders
          </h2>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 text-slate-500 hover:text-indigo-600"
                onClick={() => setCreateOpen(true)}
                data-testid={`${testid}-new-folder-button`}
                aria-label="Create new folder"
              >
                <FolderPlus size={14} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>New folder</TooltipContent>
          </Tooltip>
        </div>

        {/* md-only: a single FolderPlus button at the top of the icon rail */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-9 w-9 text-slate-500 hover:text-indigo-600 lg:hidden"
              onClick={() => setCreateOpen(true)}
              aria-label="Create new folder"
              data-testid={`${testid}-new-folder-button-rail`}
            >
              <FolderPlus size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">New folder</TooltipContent>
        </Tooltip>

        <div className="relative hidden lg:block">
          <Search size={12} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search folders…"
            className="h-7 pl-7 text-xs"
            data-testid={`${testid}-search`}
          />
        </div>

        {/* R3 Phase 6A.7.3 — Recently Used pin.  Hidden entirely when
            the audit aggregate is empty (new accounts / no drops in
            the window). */}
        {recentlyUsed.length > 0 && (
          <div
            className="hidden lg:block"
            data-testid={`${testid}-recently-used-section`}
          >
            <div className="mb-1 flex items-center gap-1 px-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              <History size={10} />
              Recently used
            </div>
            <div className="flex flex-col gap-0.5">
              {recentlyUsed.map((f) => (
                <SidebarRow
                  key={`recent-${f.folder_id}`}
                  dndId={`folder-recent-${f.folder_id}`}
                  dropData={{
                    type: "folder",
                    folder_id: f.folder_id,
                    folder_name: f.name,
                    can_modify_items: f.can_modify_items !== false,
                  }}
                  icon={<FolderIcon name={f.icon} size={14} />}
                  label={f.name}
                  tone={f.color}
                  count={f.drops_in_window}
                  selected={selectedFolderId === f.folder_id}
                  onClick={() => onSelect(f.folder_id)}
                  testid={`${testid}-recent-row-${f.folder_id}`}
                />
              ))}
            </div>
            <div className="my-1 h-px w-full bg-slate-200" />
          </div>
        )}

        {/* Special rows */}
        <SidebarRow
          dndId={`special-${SPECIAL_ALL}`}
          dropData={{ type: "special", target: SPECIAL_ALL }}
          icon={<Layers size={14} />}
          label="All workflows"
          count={allCount}
          selected={selectedFolderId == null}
          onClick={() => onSelect(null)}
          testid={`${testid}-row-all`}
          tone="slate"
        />
        <SidebarRow
          dndId={`special-${SPECIAL_UNFOLDERED}`}
          dropData={{ type: "special", target: SPECIAL_UNFOLDERED }}
          icon={<Inbox size={14} />}
          label="Unfoldered"
          count={unfolderedCount}
          selected={selectedFolderId === SPECIAL_UNFOLDERED}
          onClick={() => onSelect(SPECIAL_UNFOLDERED)}
          testid={`${testid}-row-unfoldered`}
          tone="slate"
          hint="Workflows not in any folder. Dropping here removes the workflow from every folder it currently lives in."
        />

        <div className="my-1 hidden h-px w-full bg-slate-200 lg:block" />

        <div className="flex flex-col items-stretch gap-0.5 overflow-y-auto" style={{ maxHeight: "60vh" }}>
          {foldersQuery.isLoading && (
            <p className="hidden px-2 py-1 text-[11px] text-slate-400 lg:block">
              Loading folders…
            </p>
          )}
          {!foldersQuery.isLoading && filtered.length === 0 && (
            <p className="hidden px-2 py-1 text-[11px] text-slate-400 lg:block" data-testid={`${testid}-empty`}>
              {search ? "No matches." : "No folders yet."}
            </p>
          )}
          {filtered.map((f) => (
            <SidebarRow
              key={f.folder_id}
              dndId={`folder-${f.folder_id}`}
              dropData={{
                type: "folder",
                folder_id: f.folder_id,
                folder_name: f.name,
                can_modify_items: f.can_modify_items !== false,
              }}
              icon={<FolderIcon name={f.icon} size={14} />}
              label={f.name}
              tone={f.color}
              count={f.item_count ?? f.workflow_count ?? null}
              selected={selectedFolderId === f.folder_id}
              onClick={() => onSelect(f.folder_id)}
              isRecent={recentlyUsedIds.has(f.folder_id)}
              testid={`${testid}-row-${f.folder_id}`}
            />
          ))}
        </div>
      </aside>

      <CreateFolderModal open={createOpen} onOpenChange={setCreateOpen} />
    </TooltipProvider>
  );
}

function SidebarRow({
  dndId,
  dropData,
  icon,
  label,
  count,
  selected,
  onClick,
  testid,
  tone,
  hint,
  isRecent = false,
}) {
  const { setNodeRef, isOver, active } = useDroppable({
    id: dndId,
    data: dropData,
  });
  const isWorkflowDrag = active?.data?.current?.type === "workflow-handle";
  const palette = folderColorClasses(tone || "slate");

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          ref={setNodeRef}
          type="button"
          onClick={onClick}
          data-testid={testid}
          data-drop-target-id={dndId}
          data-is-over={isOver ? "true" : "false"}
          aria-pressed={selected}
          className={[
            "group flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors",
            // md: icon-rail = square button, full-width on lg
            "md:w-9 md:justify-center md:p-0 md:h-9 lg:w-full lg:justify-start lg:px-2 lg:py-1.5 lg:h-auto",
            selected
              ? "bg-indigo-600 text-white shadow-sm hover:bg-indigo-700"
              : "text-slate-700 hover:bg-white hover:text-slate-900",
            isOver && isWorkflowDrag
              ? "ring-2 ring-indigo-400 ring-offset-1 bg-indigo-50 text-indigo-900"
              : "",
          ].join(" ")}
        >
          <span
            className={[
              "grid h-5 w-5 flex-none place-items-center rounded",
              selected
                ? "bg-white/20 text-white"
                : `${palette.bg} ${palette.text}`,
            ].join(" ")}
          >
            {icon}
          </span>
          <span className="hidden flex-1 truncate lg:block">{label}</span>
          {typeof count === "number" && (
            <span
              className={[
                "hidden rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums lg:inline-block",
                selected
                  ? "bg-white/20 text-white"
                  : "bg-slate-100 text-slate-500",
              ].join(" ")}
              data-testid={`${testid}-count`}
            >
              {count}
            </span>
          )}
          {isOver && isWorkflowDrag && (
            <span
              className="hidden rounded-full bg-indigo-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white lg:inline-block"
              data-testid={`${testid}-drop-badge`}
            >
              Drop here
            </span>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-[260px]">
        <div className="font-semibold">{label}</div>
        {typeof count === "number" && (
          <div className="text-[10px] text-slate-300">
            {count} workflow{count === 1 ? "" : "s"}
          </div>
        )}
        {hint && <p className="mt-1 text-[10px] text-slate-300">{hint}</p>}
      </TooltipContent>
    </Tooltip>
  );
}

export { SPECIAL_ALL, SPECIAL_UNFOLDERED };
